import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { UserFilters, UserTable } from "./index";
import { useUserData } from "./hooks";
import styles from "./UserManager.module.css";
import { useAuth } from "../../../../../../context/AuthContext";

/**
 * UserManager - Container component for user management functionality
 *
 * @param {Object} props - Component props
 * @param {Function} props.onUserEdit - Callback when a user edit is requested
 */
const UserManager = ({ onUserEdit }) => {
  // Get auth context
  const { token: authToken, getAuthHeaders, logout } = useAuth();

  // Default filters
  const defaultFilters = {
    daysBack: 10,
    sortBy: "userId",
    sortOrder: "desc",
  };

  // Predefined filters for quick access
  const predefinedFilters = [
    {
      name: "Recent Signups (7 days)",
      filters: { daysBack: 7 },
    },
    {
      name: "Missing GroupMe IDs",
      filters: { hasGroupMeId: false },
    },
    {
      name: "Female Users",
      filters: { sex: "female" },
    },
    {
      name: "Male Users",
      filters: { sex: "male" },
    },
  ];

  // Use the user data hook
  const {
    users,
    isLoading,
    error,
    hasMoreData,
    filters,
    handleFilterChange,
    applyFilters,
    loadMore,
    columnFilters,
    handleColumnFilterChange,
    activeFilterColumn,
    toggleFilterDropdown,
    clearFilters,
  } = useUserData({
    defaultFilters,
    authToken,
    onLogout: logout,
    getAuthHeaders,
  });

  // Handle applying a predefined filter
  const handleApplyPredefinedFilter = useCallback(
    (filter) => {
      // Apply each filter from the predefined set
      Object.entries(filter.filters).forEach(([key, value]) => {
        handleFilterChange(key, value);
      });

      // Apply the filters
      applyFilters();
    },
    [handleFilterChange, applyFilters]
  );

  return (
    <div className={styles.userManagerContainer}>
      {error && <div className={styles.errorMessage}>Error: {error}</div>}

      <UserFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onApplyPredefinedFilter={handleApplyPredefinedFilter}
        predefinedFilters={predefinedFilters}
        isLoading={isLoading}
      />

      <UserTable
        users={users}
        isLoading={isLoading}
        onUserEdit={onUserEdit}
        columnFilters={columnFilters}
        onColumnFilterChange={handleColumnFilterChange}
        activeFilterColumn={activeFilterColumn}
        onToggleFilterColumn={toggleFilterDropdown}
        onClearFilters={clearFilters}
        onLoadMore={loadMore}
        hasMoreData={hasMoreData}
      />
    </div>
  );
};

UserManager.propTypes = {
  onUserEdit: PropTypes.func.isRequired,
};

export default UserManager;
